'use client';

// 登录页面组件 - 允许用户输入账号和密码进行登录
import { useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, Card, Image, message, Tabs } from 'antd';
import {
    LockOutlined,
    UserOutlined,
    MobileOutlined,
    SafetyOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/components/infrastructure/auth-context';
import { useRouter } from 'next/navigation';
import { LoginType } from '@/services/api-types';
import { getVerificationCodeApi } from '@/services/personal-center';
import { usePublicCSS } from '@/hooks/use-public-css';

/**
 * 登录页面组件
 * 允许用户输入账号和密码进行登录
 */

export default function Page() {
    // 引入这个页面需要的 CSS
    usePublicCSS(['/assets/css/login.css']);
    const [loading, setLoading] = useState(false);
    const [activeKey, setActiveKey] = useState('1'); // 默认展示账户登录
    const [countdown, setCountdown] = useState(0); // 验证码倒计时
    const [countdownTimer, setCountdownTimer] = useState<NodeJS.Timeout | null>(
        null,
    ); // 计时器引用
    const [qrCodeUrl, setQrCodeUrl] = useState('/assets/img/qrcode.png');
    const [pollingTimer, setPollingTimer] = useState<NodeJS.Timeout | null>(
        null,
    ); // 轮询登录状态的计时器
    const [qrCodeExpired, setQrCodeExpired] = useState(false); // 二维码过期状态
    const [expireTimer, setExpireTimer] = useState<NodeJS.Timeout | null>(null); // 二维码过期计时器
    const [isClient, setIsClient] = useState(false); // 标记是否在客户端
    const [messageApi, contextHolder] = message.useMessage();

    // 在组件挂载后设置isClient为true，确保只在客户端进行条件渲染
    useEffect(() => {
        setIsClient(true);
    }, []);
    const { login } = useAuth();
    const router = useRouter();
    // 为每种登录方式创建独立的表单实例
    const [accountForm] = Form.useForm();
    const [mobileForm] = Form.useForm();

    // 定义一个函数来获取验证码并检查是否为6位数字
    const isVerificationCodeValid = (): boolean => {
        const code = mobileForm.getFieldValue('phoneyzm');
        return /^\d{6}$/.test(code || '');
    };

    // 处理登录方式切换
    const handleTabChange = (key: string) => {
        setActiveKey(key);
        // 切换登录方式时重置对应的表单
        if (key === '1') {
            accountForm.resetFields();
        } else if (key === '2') {
            mobileForm.resetFields();
        }
        clearTimers(); // 切换登录方式时清除定时器
        // 如果切换到微信登录，显示二维码
        if (key === '3') {
            showWechatQrCode();
        }
    };

    /**
     * 获取验证码方法
     */
    const getVerificationCode = async () => {
        // 验证手机号
        const phoneNumber = mobileForm.getFieldValue('telphone');

        if (!phoneNumber) {
            messageApi.error('请先输入手机号');
            return;
        }

        // 手机号格式验证
        const phoneRegex = /^1[3-9]\d{9}$/;
        if (!phoneRegex.test(phoneNumber)) {
            messageApi.error('请输入正确的手机号码');
            return;
        }

        try {
            // 启动倒计时
            startCountdown();
            // 在实际项目中，这里应该调用API发送验证码
            const result = await getVerificationCodeApi({
                loginType: LoginType.MOBILE,
                username: phoneNumber,
                origin: process.env.NEXT_PUBLIC_ORIGIN!,
            });
            if (!result?.success) {
                messageApi.error(result?.message);
            }
        } catch (error) {
            messageApi.error(
                error instanceof Error ? error.message : '获取验证码失败',
            );
        }
    };

    /**
     * 启动验证码倒计时
     */
    const startCountdown = () => {
        // 清除之前的计时器
        if (countdownTimer) {
            clearInterval(countdownTimer);
            setCountdownTimer(null);
        }

        // 设置倒计时为5分钟-300秒
        setCountdown(300);

        // 创建新的计时器
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    // 倒计时结束，清除计时器
                    clearInterval(timer);
                    setCountdownTimer(null);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        setCountdownTimer(timer);
    };

    /**
     * 显示微信二维码并开始轮询登录状态
     */
    const showWechatQrCode = async () => {
        try {
            // 重置过期状态
            setQrCodeExpired(false);

            // 清除之前的过期计时器
            if (expireTimer) {
                clearTimeout(expireTimer);
            }

            // 在实际项目中，这里应该调用API获取微信登录二维码
            console.log('获取微信二维码');

            // 为演示，使用qrcode.png作为占位图
            // 实际项目中应该调用API获取真实的二维码图片URL
            setQrCodeUrl('/assets/img/qrcode.png');

            // 开始轮询登录状态
            startLoginPolling();

            // 设置二维码60秒后过期
            const expireTimeout = setTimeout(() => {
                setQrCodeExpired(true);
            }, 60000);

            setExpireTimer(expireTimeout);
        } catch (error) {
            messageApi.error('获取微信二维码失败');
            console.error('获取微信二维码错误:', error);
        }
    };

    /**
     * 开始轮询微信登录状态
     */
    const startLoginPolling = () => {
        // 清除之前的轮询计时器
        if (pollingTimer) {
            clearInterval(pollingTimer);
        }

        // 每3秒轮询一次登录状态
        const timer = setInterval(async () => {
            try {
                // 二维码已过期，停止轮询
                if (qrCodeExpired) {
                    if (pollingTimer) {
                        clearInterval(pollingTimer);
                        setPollingTimer(null);
                    }
                    return;
                }

                // 在实际项目中，这里应该调用API检查微信登录状态
                console.log('检查微信登录状态');

                // 模拟扫码登录成功
                // 实际项目中应该根据API返回结果判断登录状态
                // 这里仅作为演示
                // const mockLoginSuccess = Math.random() > 0.8; // 20%的概率模拟登录成功
                const mockLoginSuccess = true;
                if (mockLoginSuccess) {
                    // 登录成功，清除轮询计时器
                    if (pollingTimer) {
                        clearInterval(pollingTimer);
                        setPollingTimer(null);
                    }

                    // 清除过期计时器
                    if (expireTimer) {
                        clearTimeout(expireTimer);
                        setExpireTimer(null);
                    }

                    messageApi.success('微信扫码登录成功');

                    // 延迟跳转，确保消息显示
                    await new Promise(resolve => setTimeout(resolve, 500));
                    router.push('/'); // 登录成功后重定向到首页
                }
            } catch (error) {
                console.error('检查微信登录状态错误:', error);
            }
        }, 3000);

        setPollingTimer(timer);
    };

    /**
     * 重新获取二维码
     */
    const refreshQrCode = () => {
        showWechatQrCode();
    };

    /**
     * 处理表单提交，尝试登录
     */
    // 定义登录表单数据类型
    interface AccountLoginForm {
        username: string;
        password: string;
    }

    interface MobileLoginForm {
        telphone: string;
        phoneyzm: string;
    }

    type LoginFormData = AccountLoginForm | MobileLoginForm;

    const handleSubmit = async (values: LoginFormData) => {
        setLoading(true);
        try {
            // 根据登录方式选择不同的登录参数
            if (activeKey === '1') {
                // 账号密码登录
                const accountValues = values as AccountLoginForm;
                const result = await login({
                    loginType: LoginType.ACCOUNT,
                    username: accountValues.username,
                    password: accountValues.password,
                    origin: process.env.NEXT_PUBLIC_ORIGIN!,
                });
                console.log(result);

                if (result.success) {
                    // messageApi.success(result.message || "登录成功");
                    // 延迟跳转，确保消息显示
                    // await new Promise(resolve => setTimeout(resolve, 500));
                    window.location.href = '/'; // 登录成功后重定向到首页
                } else {
                    messageApi.error(result.message || '登录失败');
                }
            } else if (activeKey === '2') {
                // 手机号验证码登录
                // 表单规则已经处理了手机号的非空和格式验证
                const mobileValues = values as MobileLoginForm;

                // 判断是否输入了验证码（这个需要保留，因为有特殊逻辑）
                if (!mobileValues.phoneyzm) {
                    // 如果没有输入验证码，调用接口获取验证码
                    getVerificationCode();
                    setLoading(false);
                    return;
                }

                // 如果已经输入了验证码，执行登录操作
                const result = await login({
                    loginType: LoginType.MOBILE,
                    username: mobileValues.telphone,
                    password: mobileValues.phoneyzm,
                    origin: process.env.NEXT_PUBLIC_ORIGIN!,
                });
                console.log('手机号验证码登录结果:', result);

                if (result.success) {
                    // messageApi.success(result.message ? result.message : '登录成功');
                    // 延迟跳转，确保消息显示
                    // await new Promise(resolve => setTimeout(resolve, 500));
                    router.push('/');
                } else {
                    messageApi.error(
                        result.message ? result.message : '登录失败',
                    );
                }
            }
        } catch (error) {
            messageApi.error(
                error instanceof Error ? error.message : '未知错误',
            );
            console.error('Login error:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearTimers = useCallback(() => {
        if (countdownTimer) {
            clearInterval(countdownTimer);
            setCountdownTimer(null);
        }
        if (pollingTimer) {
            clearInterval(pollingTimer);
            setPollingTimer(null);
        }
        if (expireTimer) {
            clearTimeout(expireTimer);
            setExpireTimer(null);
        }
    }, [countdownTimer, pollingTimer, expireTimer]);

    // 组件卸载时清除所有计时器
    useEffect(() => {
        return () => {
            clearTimers();
        };
    }, [clearTimers]);

    useEffect(() => {
        // 组件挂载时预加载首页
        router.prefetch('/');
    }, [router]);
    return (
        <div className="login">
            {contextHolder} {/*消息的渲染容器 */}
            <Card
                style={{
                    width: 400,
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-card)',
                }}
            >
                <div
                    style={{
                        textAlign: 'center',
                        marginBottom: '24px',
                    }}
                >
                    <Image
                        src="/assets/img/logo192.png"
                        alt="NannyGoose"
                        style={{
                            boxShadow: 'var(--shadow-extra)',
                            backgroundColor: 'var(--gray-3)',
                            width: '64px',
                            height: '64px',
                            borderRadius: '5px',
                        }}
                    />
                </div>
                <Tabs
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    style={{ marginBottom: '0' }}
                    items={[
                        {
                            key: '1',
                            label: '账号密码登录',
                            children: (
                                <Form
                                    form={accountForm}
                                    name="account-login-form"
                                    onFinish={handleSubmit}
                                >
                                    <Form.Item
                                        name="username"
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入账号',
                                            },
                                        ]}
                                        style={{ marginBottom: '16px' }}
                                    >
                                        <Input
                                            prefix={
                                                <UserOutlined className="site-form-item-icon" />
                                            }
                                            placeholder="账号"
                                            autoComplete="username"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="password"
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入密码',
                                            },
                                        ]}
                                        style={{ marginBottom: '24px' }}
                                    >
                                        <Input
                                            prefix={
                                                <LockOutlined className="site-form-item-icon" />
                                            }
                                            type="password"
                                            placeholder="密码"
                                            autoComplete="current-password"
                                        />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '16px',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            {loading ? '登录中...' : '登录'}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            ),
                        },
                        {
                            key: '2',
                            label: '手机号登录',
                            disabled: true,
                            children: (
                                <Form
                                    form={mobileForm}
                                    name="mobile-login-form"
                                    onFinish={handleSubmit}
                                >
                                    <Form.Item
                                        name="telphone"
                                        rules={[
                                            {
                                                required: true,
                                                message: '请输入手机号',
                                            },
                                            {
                                                pattern: /^1[3-9]\d{9}$/,
                                                message: '请输入正确的手机号码',
                                            },
                                        ]}
                                        style={{ marginBottom: '16px' }}
                                    >
                                        <Input
                                            prefix={
                                                <MobileOutlined className="site-form-item-icon" />
                                            }
                                            placeholder="手机号"
                                            autoComplete="telphone"
                                            maxLength={11}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="phoneyzm"
                                        rules={[
                                            // { required: true, message: "请输入验证码" },
                                            {
                                                pattern: /^\d{6}$/,
                                                message: '请输入6位数字验证码',
                                            },
                                        ]}
                                        style={{ marginBottom: '24px' }}
                                    >
                                        <Input
                                            prefix={
                                                <SafetyOutlined className="site-form-item-icon" />
                                            }
                                            type="text"
                                            placeholder="验证码"
                                            maxLength={6}
                                            suffix={
                                                isClient ? (
                                                    <div
                                                        className={`getVerificationCode ${countdown > 0 ? 'disabled' : ''}`}
                                                        onClick={() => {
                                                            if (countdown === 0)
                                                                getVerificationCode();
                                                        }}
                                                    >
                                                        {countdown > 0
                                                            ? `${countdown}秒后重发`
                                                            : '获取验证码'}
                                                    </div>
                                                ) : (
                                                    <div className="getVerificationCode">
                                                        获取验证码
                                                    </div>
                                                )
                                            }
                                        />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={loading}
                                            disabled={
                                                isClient
                                                    ? countdown > 0 &&
                                                      !isVerificationCodeValid()
                                                    : false
                                            }
                                            style={{
                                                width: '100%',
                                                height: '40px',
                                                fontSize: '16px',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            {loading ? '登录中...' : '登录'}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            ),
                        },
                        {
                            key: '3',
                            label: '微信扫码登录',
                            disabled: true,
                            children: (
                                <div style={{ textAlign: 'center' }}>
                                    {/* <h3 style={{ marginBottom: '20px' }}>微信扫码登录</h3> */}
                                    <div
                                        style={{
                                            position: 'relative',
                                            display: 'inline-block',
                                        }}
                                    >
                                        <Image
                                            src={qrCodeUrl}
                                            alt="微信登录二维码"
                                            style={{
                                                width: '150px',
                                                height: '150px',
                                            }}
                                        />
                                        {isClient && qrCodeExpired && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '150px',
                                                    height: '150px',
                                                    backgroundColor:
                                                        'rgba(0, 0, 0, 0.6)',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '4px',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={refreshQrCode}
                                            >
                                                <ReloadOutlined
                                                    style={{
                                                        fontSize: '24px',
                                                        marginBottom: '8px',
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    二维码已过期
                                                </span>
                                                <Button
                                                    type="link"
                                                    style={{
                                                        color: 'var(--primary-6)',
                                                        padding: '0',
                                                        marginTop: '4px',
                                                    }}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        refreshQrCode();
                                                    }}
                                                >
                                                    点击重新获取
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                    <p
                                        style={{
                                            color: 'var(--gray-14)',
                                            marginTop: '8px',
                                        }}
                                    >
                                        请使用微信扫码登录
                                    </p>
                                    <p
                                        style={{
                                            color: 'var(--gray-8)',
                                            fontSize: '12px',
                                            marginTop: '5px',
                                        }}
                                    >
                                        二维码有效期为60秒，过期后请重新获取
                                    </p>
                                </div>
                            ),
                        },
                    ]}
                />
            </Card>
            {/* 备案信息 */}
            <div className="copyright">
                <a
                    href="https://beian.miit.gov.cn"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    吉ICP备2023003367号-5
                </a>
            </div>
        </div>
    );
}
